import { FC, Fragment, ReactNode } from 'react';

import { Link, NodeTypes, RichTextNode } from '@purpur/library';
import type { EntryFields } from 'contentful';

import { convertContentfulRichTextToPurpur } from './utils';

const isValueWhitespaceOnly = (value: string): boolean => {
    return !value.replace(/\s+/g, '');
};

const isNodeOrContentEmpty = <T,>(node: RichTextNode<T>): boolean => {
    const { content, value } = node;

    if (!content) {
        return typeof value === 'string' && isValueWhitespaceOnly(value);
    }

    for (const item of content) {
        if (item.type === 'hyperlink') {
            return false;
        }
    }

    return content.length > 0 && typeof content[0].value === 'string' && isValueWhitespaceOnly(content[0].value);
};

const markTypes = {
    bold: (children: ReactNode) => <b>{children}</b>,
    italic: (children: ReactNode) => <i>{children}</i>,
    underline: (children: ReactNode) => <u>{children}</u>,
    code: (children: ReactNode) => <code>{children}</code>,
};

type Mark = keyof typeof markTypes;

const getNodeTypes = (isNegative?: boolean): NodeTypes => ({
    'rich-text-document': (_, children) => children,
    document: (_, children) => children,
    text: (node) => {
        const { value: nodeValue, marks } = node;
        const value = typeof nodeValue === 'string' ? nodeValue.replace(/\n/g, '<br />') : nodeValue;

        const element = <span dangerouslySetInnerHTML={{ __html: value as string }} />;

        if (!marks) {
            return element;
        }

        return marks.reduce((accumulator: ReactNode, currentValue: Mark) => {
            return markTypes[currentValue](accumulator);
        }, element);
    },
    hyperlink: ({ uri }, children) => {
        return (
            <Link variant="text" negative={isNegative}>
                <a href={uri}>{children}</a>
            </Link>
        );
    },
    paragraph: (_, children) => children,
    'header-2': (_, children) => children,
    'header-3': (_, children) => children,
    'header-4': (_, children) => children,
    'header-5': (_, children) => children,
    'header-6': (_, children) => children,
    'horizontal-row': () => null,
    table: (_, children) => children,
    'table-row': (_, children) => children,
    'table-header-cell': (_, children) => children,
    'table-cell': (_, children) => children,
    'table-body': (_, children) => children,
    'list-item': (_, children) => children,
    'ordered-list': (_, children) => children,
    'unordered-list': (_, children) => children,
    blockquote: (_, children) => children,
});

const serializeRichText = <T,>(rootNode: RichTextNode<T>, isNegative?: boolean): ReactNode => {
    let index = 0;

    const nodeTypes = getNodeTypes(isNegative);

    const renderNode = (node: RichTextNode<T>): ReactNode => {
        // node types that are missing are never rendered
        if (!node.type || typeof node.type !== 'string' || (node.type && !nodeTypes[node.type])) {
            return null;
        }

        if (isNodeOrContentEmpty(node)) {
            return null;
        }

        const children = node.content ? node.content.map(renderNode) : [];

        return <Fragment key={index++}>{nodeTypes[node.type](node, children)}</Fragment>;
    };

    return renderNode(rootNode);
};

type Props = {
    richText: EntryFields.RichText;
    isNegative?: boolean;
};

/**
 * Based on Purpur RichText, just returns content in fragment and without block elements
 */
export const InlineRichText: FC<Props> = ({ richText, isNegative }) => {
    if (!richText) {
        return null;
    }
    const convertedRichText = convertContentfulRichTextToPurpur(richText);

    if (!convertedRichText) {
        return null;
    }

    return <>{serializeRichText(convertedRichText, isNegative)}</>;
};
